import React from 'react';
import { cn } from '@/lib/utils';

export interface CinematicHeadingProps extends React.HTMLAttributes<HTMLElement> {
  level?: 'hero' | 'section' | 'card' | 'subtitle' | 'body';
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  gradient?: boolean;
  gradientVariant?: 'primary' | 'accent';
  uppercase?: boolean;
  tracking?: 'tight' | 'normal' | 'wide';
}

export const CinematicHeading = React.forwardRef<HTMLElement, CinematicHeadingProps>(
  ({ 
    className, 
    level = 'body', 
    as,
    gradient = false,
    gradientVariant = 'primary',
    uppercase = false,
    tracking = 'tight',
    children,
    ...props 
  }, ref) => {
    // Map level to default element
    const defaultElements = {
      hero: 'h1',
      section: 'h2',
      card: 'h3',
      subtitle: 'h4',
      body: 'p',
    };

    const Component = as || defaultElements[level];

    const styles = {
      hero: cn(
        'font-display font-bold leading-tight',
        'text-4xl sm:text-5xl md:text-6xl lg:text-7xl'
      ),
      section: cn(
        'font-display font-bold leading-tight',
        'text-3xl sm:text-4xl md:text-5xl'
      ),
      card: cn(
        'font-display font-bold leading-tight',
        'text-xl sm:text-2xl'
      ),
      subtitle: cn(
        'font-display font-semibold leading-snug',
        'text-lg sm:text-xl'
      ),
      body: cn(
        'font-sans font-medium leading-relaxed',
        'text-base'
      ),
    };

    const trackingStyles = {
      tight: 'tracking-tight',
      normal: 'tracking-normal',
      wide: 'tracking-wide',
    };

    const gradientStyles = gradient ? {
      primary: 'bg-clip-text text-transparent bg-gradient-to-r from-primary to-orange-400',
      accent: 'bg-clip-text text-transparent bg-gradient-to-r from-accent to-primary',
    }[gradientVariant] : '';

    return (
      <Component
        ref={ref as any}
        className={cn(
          styles[level],
          trackingStyles[tracking],
          gradient && gradientStyles,
          uppercase && 'uppercase',
          !gradient && !gradientStyles && 'text-foreground',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CinematicHeading.displayName = 'CinematicHeading';

// Convenience exports for specific heading levels
export const HeroHeading = (props: Omit<CinematicHeadingProps, 'level'>) => (
  <CinematicHeading level="hero" {...props} />
);

export const SectionHeading = (props: Omit<CinematicHeadingProps, 'level'>) => (
  <CinematicHeading level="section" {...props} />
);

export const CardHeading = (props: Omit<CinematicHeadingProps, 'level'>) => (
  <CinematicHeading level="card" {...props} />
);

export const Subtitle = (props: Omit<CinematicHeadingProps, 'level'>) => (
  <CinematicHeading level="subtitle" {...props} />
);

export default CinematicHeading;
